// src/pages/document/DocumentAcknowledgmentListPage.tsx

import { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useUserprofile, useUser, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';
import { UserPicker } from '@/components/UserPicker';
import { formatDate, formatRelativeTime, getInitials, getAvatarColor } from '@/features/document/logic';

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

type AckStatus = 'PENDING' | 'ACKNOWLEDGED' | 'OVERDUE' | 'WAIVED';

const STATUS_CONFIG: Record<AckStatus, { variant: string; labelKey: string; icon: string }> = {
  PENDING: {
    variant: 'warning',
    labelKey: 'document.ackPending',
    icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  },
  ACKNOWLEDGED: {
    variant: 'success',
    labelKey: 'document.ackAcknowledged',
    icon: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3',
  },
  OVERDUE: {
    variant: 'error',
    labelKey: 'document.ackOverdue',
    icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 8v4M12 16h.01',
  },
  WAIVED: {
    variant: 'secondary',
    labelKey: 'document.ackWaived',
    icon: 'M18 6L6 18M6 6l12 12',
  },
};

type StatusFilter = 'ALL' | AckStatus;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getActorName(item: any, profileMap: Map<string, any>, userMap: Map<string, any>): string {
  // Try display_name from acknowledgment itself
  if (item.actor?.display_name) return item.actor.display_name;
  // Look up in profile store
  const actorId = item.actor?.id ?? item.actor?.entity?.id ?? item.actor;
  if (typeof actorId === 'string') {
    const profile = profileMap.get(actorId);
    if (profile) {
      if (profile.display_name) return profile.display_name;
      const full = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
      if (full) return full;
      const user = userMap.get(profile.user?.id);
      if (user?.username) return user.username;
      if (user?.email) return user.email;
    }
    // Direct user lookup
    const user = userMap.get(actorId);
    if (user?.username) return user.username;
    if (user?.email) return user.email;
    return actorId.substring(0, 8);
  }
  return '?';
}

function isDueSoon(dueDate: string | undefined): boolean {
  if (!dueDate) return false;
  const diff = new Date(dueDate).getTime() - Date.now();
  return diff > 0 && diff < 3 * 24 * 60 * 60 * 1000; // 3 days
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({ icon, label, count, variant, active, onClick }: {
  icon: string;
  label: string;
  count: number;
  variant: string;
  active: boolean;
  onClick: () => void;
}) {
  const colorMap: Record<string, string> = {
    warning: 'text-amber-500 bg-amber-500/10',
    success: 'text-emerald-500 bg-emerald-500/10',
    error: 'text-red-500 bg-red-500/10',
    secondary: 'text-slate-400 bg-slate-400/10',
    default: 'text-blue-500 bg-blue-500/10',
  };
  const colors = colorMap[variant] || colorMap.default;

  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all
        ${active
          ? 'border-ring bg-accent shadow-sm'
          : 'border-border bg-card hover:border-ring/50 hover:bg-accent/50'}
      `}
    >
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${colors}`}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d={icon} />
        </svg>
      </div>
      <div>
        <div className="text-xl font-semibold text-foreground leading-tight">{count}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </button>
  );
}

function ProgressBar({ acknowledged, total }: { acknowledged: number; total: number }) {
  const { t } = useI18n();
  if (total === 0) return null;
  const pct = Math.round((acknowledged / total) * 100);
  const allDone = acknowledged === total;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {allDone
            ? t('document.ackAllDone')
            : t('document.ackProgress', { count: acknowledged, total })}
        </span>
        <span className={`text-sm font-medium ${allDone ? 'text-emerald-500' : 'text-foreground'}`}>
          {pct}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${allDone ? 'bg-emerald-500' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function AckRow({ item, profileMap, userMap, locale }: {
  item: any;
  profileMap: Map<string, any>;
  userMap: Map<string, any>;
  locale: string;
}) {
  const { t } = useI18n();
  const status = (item.status as AckStatus) || 'PENDING';
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  const name = getActorName(item, profileMap, userMap);
  const initials = getInitials(name);
  const color = getAvatarColor(name);
  const dueSoon = status === 'PENDING' && isDueSoon(item.due_date);

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors">
      {/* Avatar */}
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
        style={{ backgroundColor: color }}
      >
        {initials}
      </div>

      {/* Name + comment */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground truncate">{name}</span>
          {item.document_version && (
            <span className="text-xs text-muted-foreground">v{item.document_version}</span>
          )}
        </div>
        {item.comment && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.comment}</p>
        )}
      </div>

      {/* Due date warning */}
      {dueSoon && (
        <div className="flex items-center gap-1 text-amber-500 flex-shrink-0" title={t('document.ackDueDate')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 8v4M12 16h.01" />
          </svg>
          <span className="text-xs font-medium">{formatDate(item.due_date, locale)}</span>
        </div>
      )}

      {/* Status badge */}
      <Badge variant={config.variant as any} size="sm">
        {t(config.labelKey)}
      </Badge>

      {/* Date */}
      <div className="text-xs text-muted-foreground text-right flex-shrink-0 w-24">
        {status === 'ACKNOWLEDGED'
          ? formatRelativeTime(item.acknowledged_at, locale)
          : formatRelativeTime(item.requested_at, locale)}
      </div>
    </div>
  );
}

function EmptyState({ onRequest }: { onRequest: () => void }) {
  const { t } = useI18n();

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      </div>
      <h3 className="text-base font-medium text-foreground mb-1">{t('document.ackEmptyTitle')}</h3>
      <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
        {t('document.ackEmptyDescription')}
      </p>
      <Button onClick={onRequest}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="mr-2">
          <path d="M12 5v14M5 12h14" />
        </svg>
        {t('document.requestAcknowledgment')}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const DocumentAcknowledgmentListPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useDocument();
  const profileStore = useUserprofile();
  const userStore = useUser();
  const { t, locale } = useI18n();

  const [acknowledgments, setAcknowledgments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestData, setRequestData] = useState({ actorId: '', actorName: '', due_date: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  // Load profiles for actor name resolution
  useEffect(() => {
    if (profileStore.items.length === 0 && !profileStore.isLoading) {
      profileStore.ListUserprofiles({ sort: [], pagination: { limit: 50, cursor: '', direction: 'forward' } });
    }
    if (userStore.items.length === 0 && !userStore.isLoading) {
      userStore.ListUsers({ sort: [], pagination: { limit: 50, cursor: '', direction: 'forward' } });
    }
  }, []);

  const profileMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const p of profileStore.items || []) {
      if (p.user?.id) map.set(p.user.id, p);
      if (p.entity?.id) map.set(p.entity.id, p);
    }
    return map;
  }, [profileStore.items]);

  const userMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const u of userStore.items || []) {
      if (u.entity?.id) map.set(u.entity.id, u);
    }
    return map;
  }, [userStore.items]);

  // Load acknowledgments
  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await store.ListAcknowledgments('document', id);
        setAcknowledgments(response?.acknowledgments?.acknowledgments ?? []);
      } catch {
        // Error handled by store
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id]);

  // Computed stats
  const stats = useMemo(() => {
    const total = acknowledgments.length;
    const pending = acknowledgments.filter((a: any) => a.status === 'PENDING').length;
    const acknowledged = acknowledgments.filter((a: any) => a.status === 'ACKNOWLEDGED').length;
    const overdue = acknowledgments.filter((a: any) => a.status === 'OVERDUE').length;
    const waived = acknowledgments.filter((a: any) => a.status === 'WAIVED').length;
    return { total, pending, acknowledged, overdue, waived };
  }, [acknowledgments]);

  // Filtered list
  const filtered = useMemo(() => {
    if (statusFilter === 'ALL') return acknowledgments;
    return acknowledgments.filter((a: any) => a.status === statusFilter);
  }, [acknowledgments, statusFilter]);

  const reloadAcknowledgments = async () => {
    if (!id) return;
    const response = await store.ListAcknowledgments('document', id);
    setAcknowledgments(response?.acknowledgments?.acknowledgments ?? []);
  };

  const handleRequestAcknowledgment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !requestData.actorId) return;
    setIsSubmitting(true);
    try {
      await store.RequestAcknowledgment('document', id, {
        actors: {
          actors: [{ entity: { id: requestData.actorId, type_id: '', type_name: 'profile', platform_id: '', tenant_id: '', product_id: '' }, display_name: requestData.actorName, avatar: '' }],
        },
        due_date: requestData.due_date ? new Date(requestData.due_date).toISOString() : undefined,
        message: requestData.message || undefined,
      });
      setShowRequestForm(false);
      setRequestData({ actorId: '', actorName: '', due_date: '', message: '' });
      await reloadAcknowledgments();
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!id) return;
    setIsSubmitting(true);
    try {
      await store.AcknowledgeDocument('document', id, {});
      await reloadAcknowledgments();
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-6 space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Link to="/documents" className="hover:text-foreground transition-colors">
          {t('document.list')}
        </Link>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
          <path d="M9 18l6-6-6-6" />
        </svg>
        <Link to={`/documents/${id}`} className="hover:text-foreground transition-colors">
          {t('document.details')}
        </Link>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
          <path d="M9 18l6-6-6-6" />
        </svg>
        <span className="text-foreground">{t('document.acknowledgments')}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('document.acknowledgments')}</h1>
          {stats.total > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              {t('document.ackProgress', { count: stats.acknowledged, total: stats.total })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleAcknowledge} disabled={isSubmitting}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="mr-1.5">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3" />
            </svg>
            {t('document.acknowledge')}
          </Button>
          <Button onClick={() => setShowRequestForm(!showRequestForm)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="mr-1.5">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {t('document.requestAcknowledgment')}
          </Button>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Request form */}
      {showRequestForm && (
        <form onSubmit={handleRequestAcknowledgment}>
          <Card>
            <CardHeader>
              <CardTitle>{t('document.requestAcknowledgment')}</CardTitle>
              <CardDescription>{t('document.requestAcknowledgmentDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <UserPicker
                label={t('document.ackActor')}
                value={requestData.actorId}
                onChange={(userId, displayName) =>
                  setRequestData((prev) => ({ ...prev, actorId: userId, actorName: displayName }))
                }
                placeholder={t('document.ackActorPlaceholder')}
              />
              <Input
                label={t('document.ackDueDate')}
                type="date"
                value={requestData.due_date}
                onChange={(e) => setRequestData((prev) => ({ ...prev, due_date: e.target.value }))}
                placeholder={t('document.ackDueDatePlaceholder')}
              />
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-foreground">{t('document.ackComment')}</label>
                <textarea
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[80px]"
                  value={requestData.message}
                  onChange={(e) => setRequestData((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder={t('document.ackCommentPlaceholder')}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => setShowRequestForm(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting || !requestData.actorId}>
                {isSubmitting ? t('common.submitting') : t('document.ackSendRequest')}
              </Button>
            </CardFooter>
          </Card>
        </form>
      )}

      {/* Stats + progress */}
      {!isLoading && stats.total > 0 && (
        <>
          {/* Progress bar */}
          <ProgressBar acknowledged={stats.acknowledged} total={stats.total} />

          {/* Stat cards as filters */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              icon="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
              label={t('document.ackFilterAll')}
              count={stats.total}
              variant="default"
              active={statusFilter === 'ALL'}
              onClick={() => setStatusFilter('ALL')}
            />
            <StatCard
              icon={STATUS_CONFIG.PENDING.icon}
              label={t('document.ackPending')}
              count={stats.pending}
              variant="warning"
              active={statusFilter === 'PENDING'}
              onClick={() => setStatusFilter('PENDING')}
            />
            <StatCard
              icon={STATUS_CONFIG.ACKNOWLEDGED.icon}
              label={t('document.ackAcknowledged')}
              count={stats.acknowledged}
              variant="success"
              active={statusFilter === 'ACKNOWLEDGED'}
              onClick={() => setStatusFilter('ACKNOWLEDGED')}
            />
            <StatCard
              icon={STATUS_CONFIG.OVERDUE.icon}
              label={t('document.ackOverdue')}
              count={stats.overdue}
              variant="error"
              active={statusFilter === 'OVERDUE'}
              onClick={() => setStatusFilter('OVERDUE')}
            />
          </div>
        </>
      )}

      {/* Acknowledgment list */}
      {isLoading ? (
        <Card>
          <div className="divide-y divide-border">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3 animate-pulse">
                <div className="w-9 h-9 rounded-full bg-muted flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-32 rounded bg-muted" />
                  <div className="h-3 w-48 rounded bg-muted" />
                </div>
                <div className="h-5 w-16 rounded-full bg-muted" />
                <div className="h-3 w-16 rounded bg-muted" />
              </div>
            ))}
          </div>
        </Card>
      ) : stats.total === 0 ? (
        <Card>
          <EmptyState onRequest={() => setShowRequestForm(true)} />
        </Card>
      ) : (
        <Card>
          {filtered.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">{t('common.noResults')}</p>
            </div>
          ) : (
            <div>
              {filtered.map((item: any, idx: number) => (
                <AckRow
                  key={item.entity?.id || item.id || idx}
                  item={item}
                  profileMap={profileMap}
                  userMap={userMap}
                  locale={locale}
                />
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
});

export default DocumentAcknowledgmentListPage;
